import { MoneyValues, PaymentMethod, ServiceFee } from './TicketApi';

export type GuestListRequestPaymentStatus =
  | 'not_required'
  | 'pending'
  | 'paid'
  | 'failed';

export interface AppliedFee {
  amount: MoneyValues;
  fee: {
    name: string;
    amountPerUnit?: MoneyValues;
    percentage?: number;
  } & ServiceFee;
}

export interface GuestListRequest {
  id: string;
  name: string;
  email: string;
  places: number;
  status: 'approved_pending_payment' | 'paid';
  paymentStatus: GuestListRequestPaymentStatus;
  orderId: string | null;
  eventName: string;
  formName: string;
  feeDescription?: string | null;
  appliedFees: AppliedFee[] | null;
  totalAmount: MoneyValues | null;
  paymentMethods: PaymentMethod[] | null;
}

export interface GuestListRequestResponse {
  data: GuestListRequest;
}

export interface PaymentResponse {
  type: 'redirect';
  url: string;
}

export class GuestListRequestNotFoundError extends Error {
  constructor() {
    super('GuestListRequestNotFound');
    this.name = 'GuestListRequestNotFoundError';
  }
}

export class GuestListRequestAlreadyPaidError extends Error {
  constructor() {
    super('GuestListRequestAlreadyPaid');
    this.name = 'GuestListRequestAlreadyPaidError';
  }
}

export class GuestListRequestApi {
  constructor(private readonly baseUrl: string) {}

  public async getRequest(requestId: string): Promise<GuestListRequest> {
    const res = await fetch(`${this.baseUrl}/guest-list-requests/${requestId}`, {
      cache: 'no-cache',
      headers: {
        Accept: 'application/json',
      },
    });

    if (res.status === 404) {
      throw new GuestListRequestNotFoundError();
    }

    if (!res.ok) {
      throw new Error(`Failed to load guestlist request (${res.status})`);
    }

    const body: GuestListRequestResponse = await res.json();
    return body.data;
  }

  public async pay(
    requestId: string,
    paymentMethodId: string,
    returnUrl: string,
  ): Promise<PaymentResponse> {
    const res = await fetch(
      `${this.baseUrl}/guest-list-requests/${requestId}/pay`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ paymentMethodId, returnUrl }),
      },
    );

    if (res.status === 409) {
      throw new GuestListRequestAlreadyPaidError();
    }

    if (!res.ok) {
      throw new Error(`Payment initiation failed (${res.status})`);
    }

    return await res.json();
  }
}
